import EventEmitter from "eventemitter3"
import { ItemNode } from "./components/grid/ItemNode"
import { AppData, Log, Router } from "@lightningjs/sdk"
import { AppSetup } from "./app-setup"
import { Helper } from "./helper"
import { FTStreamingPlayer } from "./components/ft-streaming-player"
import { ContentPlayer } from "./components/ft-streaming-player/content-player"
import GridViewModel from "./components/grid/grid-view-model"
import { GRIDTYPE } from "./enum"


export type AutoplayConfig = {
    gridDelay: number
    // prefetchTime: number
    stopOnInteraction: boolean
    gridType?: 'main' | 'mlp'
}


/**
* @class Autoplay
* @description Autoplay class to manage the autoplay functionality of the app.
* It handles the autoplay of videos in grid and mlp.
* It also manages the user interaction and stops the autoplay if required.
* @extends EventEmitter
*/
export class Autoplay extends EventEmitter {
    mlpStack: GridViewModel[] = []
    private gridTimeout: number | null = null
    private videoTimeout: number | null = null
    private handleStartDebounceTimer: number | null = null;
    private previewStartTime: number | null = null;
    private blockedItemDelay: number = 5000;

    private config: AutoplayConfig = {
        gridDelay: 5000,
        stopOnInteraction: false,
        gridType: 'main'
    }
    private playState = { shouldPlay: false, playTrailer: false, transition: false };
    contentPlayer!: ContentPlayer
    ftStreamingPlayer!: FTStreamingPlayer
    videoElWrapper!: HTMLElement
    videoEl!: HTMLVideoElement
    isAutoplayStart = false
    playingVodInfo!: ItemNode
    focusVodInfo!: ItemNode
    isAutoPlayGridTimer: boolean = false
    isAutoPlayGridVideoReady: boolean = false
    /** True while a focused live item is counting down towards the live player. No preview is loaded in this state. */
    isLiveAutoplayPending: boolean = false

    private autoplayBackPresses: number[] = [];
    private autoplayDisabledUntil: number | null = null;

    constructor(_config: AutoplayConfig) {
        super()
        this.config = {
            ...this.config,
            ..._config,
        }
        this.setupGridPlayer()
        window.ftAutoplay = this
    }

    /** 
    * @method setupGridPlayer
    * @description Setup the grid player by initializing the content player and video elements.
    * It sets up the video element and its wrapper for playback.
    * 
    */
    setupGridPlayer() {
        this.contentPlayer = ContentPlayer.getInstance()
        this.contentPlayer._setupVideoPlayer()
        this.videoEl = this.contentPlayer.getVideoEl()
        this.videoElWrapper = this.contentPlayer.getVideoElWrapper()
        this.ftStreamingPlayer = this.contentPlayer.getStreamingPlayer()

    }

    /**
     * @method isLiveItem
     * @description Whether the given node is live content. Live items never get a hero-image preview.
     */
    private isLiveItem(videoData: ItemNode): boolean {
        return videoData?.feed_type == 'live'
    }

    /**
     * @method isGridAutoplayEnabled
     * @description Backend switch that governs autoplay for the grid this instance is attached to.
     */
    private isGridAutoplayEnabled(): boolean {
        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig();

        if (this.config.gridType === 'mlp') {
            return appApiConfig?.appConfig?.mlpAutoplay === true;
        }
        return appApiConfig?.appConfig?.heroImageAutoplay === true;
    }

    /**
     * @method isAutoplayIgnored
     * @description Whether this node has already been autoplayed in this session and must not be autoplayed again.
     */
    private isAutoplayIgnored(videoData: ItemNode): boolean {
        const { appState } = AppData as AppSetup;
        if (!videoData?.node_id) return false;
        return appState?.player?.ignoredAutoplayNodes?.includes(videoData.node_id) === true;
    }

    /**
     * @method markAutoplayIgnored
     * @description Marks a node as consumed by autoplay for the rest of the session, so that returning
     * to the grid with focus on the same item does not autoplay it again.
     */
    markAutoplayIgnored(videoData: ItemNode) {
        const { appState } = AppData as AppSetup;
        if (!videoData?.node_id || !appState?.player) return;
        if (!appState.player.ignoredAutoplayNodes) appState.player.ignoredAutoplayNodes = [];
        if (!appState.player.ignoredAutoplayNodes.includes(videoData.node_id)) {
            appState.player.ignoredAutoplayNodes.push(videoData.node_id);
        }
    }

    private getPlayState(videoData: ItemNode) {
        if (!videoData) return { shouldPlay: false, playTrailer: false, transition: false };

        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig();

        const enableTrailerAPI = appApiConfig?.appConfig?.enableTrailer === true;
        const hasTrailer = !!videoData?.trailer_url;
        const trailersPlayedInSession = appState?.player?.trailersPlayedInSession ?? [];
        const trailerPlayedForContent = trailersPlayedInSession.includes(videoData.node_id);

        const isGridAutoplayEnabled = this.isGridAutoplayEnabled();

        let shouldPlay = false;
        let playTrailer = false;
        let transition = false;

        if (this.isAutoplayIgnored(videoData)) {
            return { shouldPlay: false, playTrailer: false, transition: false };
        }

        if (enableTrailerAPI && !trailerPlayedForContent) {
            if (hasTrailer) {
                shouldPlay = true;
                playTrailer = true;
                transition = isGridAutoplayEnabled;
            } else {
                if (isGridAutoplayEnabled) {
                    shouldPlay = true;
                    playTrailer = false;
                    transition = true;
                } else {
                    shouldPlay = false;
                }
            }
        } else {
            if (isGridAutoplayEnabled) {
                shouldPlay = true;
                playTrailer = false;
                transition = true;
            } else {
                shouldPlay = false;
            }
        }

        return { shouldPlay, playTrailer, transition };
    }

    private _getCurrentRowType(): string | null {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        const currentGrid = app?.navigation.getCurrentGrid();
        
        if (currentGrid && currentGrid.uiList) {
            const focusInfo = currentGrid.getFocusInfo();
            const currentRowIndex = focusInfo.focusRow;
            const currentRow = currentGrid.uiList[currentRowIndex];
            
            if (currentRow && currentRow.data && currentRow.data.item && 
                currentRow.data.item.rowList) {
                return currentRow.data.item.rowList.row_type
            }
        }
        return null
    }

    
    /**
     * @method isItemBlockedForAutoplay
     * @description Reusable helper to determine whether an item should be skipped during autoplay due to being blocked or adult content.
     * Live items are no longer treated as blocked here - they are autoplayed through `startLiveAutoplay` instead.
     * @param app - AppModel instance
     * @param appConfig - AppConfig instance
     * @param vodInfo - ItemNode to check
     */
    private isItemBlockedForAutoplay(app: any, appConfig: any, vodInfo: ItemNode): boolean {
        return app?.blockingManager.isItemBlocked(vodInfo);
    }

    /** 
     * @method start
     * @description Initiates the autoplay workflow for the currently focused grid node.
     * - Validates active widgets and grid states to ensure autoplay is permitted.
     * - Hands Live TV items over to `startLiveAutoplay`, which skips the hero preview entirely.
     * - If the item is blocked, skips preloading and configures the grid timer to automatically shift focus to the next unblocked item using `blockedItemDelay`.
     * - Otherwise, preloads the required video and relies on `gridTimeout` to transition to full playback once `gridDelay` expires.
     */
    start() {
        this.stop()
        const { appConfig, appState } = AppData as AppSetup;
        const app = appState?.getApp()

        const activeWidget = Router.getActiveWidget()
        if (activeWidget) {
            Log.info('Autoplay disabled because a widget is active', activeWidget);
            return
        }

        if (this.isAutoplayTemporarilyDisabled()) {
            return
        }
        
        // Check if current grid is TopCarousel - if so, don't start autoplay
        const currentGrid = app?.navigation.getCurrentGrid()
        if (currentGrid?.type === GRIDTYPE.TOP_CAROUSEL) {
            Log.info('Autoplay disabled for TopCarousel grid');
            return
        }
        
        // Check if current row is non-click - if so, don't start autoplay (only when mid carousel is enabled)
        if (appConfig?.enableMidCarousel && this._getCurrentRowType() === 'non-click') {
            Log.info('Autoplay disabled for non-click row');
            return
        }

        // Check if current row is carousel - if so, don't start autoplay if carousel autoplay is disabled
        if (appConfig?.enableMidCarousel && !appConfig?.enableMidCarouselAutoplay && this._getCurrentRowType() === 'carousel') {
            Log.info('Autoplay disabled for Mid Carousel row');
            return
        }
        
        const vodInfo = app?.navigation.getCurrentVodInfo() as ItemNode
        this.focusVodInfo = vodInfo

        if (!vodInfo) {
            Log.info('No vodInfo found, stopping autoplay');
            return
        }
        if (this.isLiveItem(vodInfo)) {
            this.startLiveAutoplay(vodInfo)
            return
        }
        const isBlocked = this.isItemBlockedForAutoplay(app, appConfig, vodInfo);

        if (!isBlocked) {
            this.emit("onStart")
            Log.info('Autoplay: , GridTimerStart', performance.now());
            this.getAutoplayItem()
        } else {
            Log.info(`Autoplay: Blocked item focused. Waiting ${this.blockedItemDelay/1000}s to auto-shift.`);
        }

        this.gridTimeout = window.setTimeout(() => {
            if (isBlocked) {
                if (currentGrid) {
                    const focusInfo = currentGrid.getFocusInfo();
                    const rowLength = currentGrid._getRowItemLength(focusInfo.focusRow);
                    this.scanAndSkipBlockedRowItems(currentGrid, app, appConfig, focusInfo, rowLength);
                }
                return;
            }

            const videoToPlay = this.playingVodInfo || this.focusVodInfo;
            const playState = this.getPlayState(videoToPlay);

            if (!playState.shouldPlay) {
                Log.info('Autoplay disabled at gridTimeout because constraints not met');
                return;
            }

            this.emit("onGridAutoplay", videoToPlay, playState.playTrailer)
            Log.info('Autoplay: , GridTimerExecuted', performance.now());

            // Play the preloaded video immediately
            // this.ftStreamingPlayer.play()
            this.isAutoPlayGridTimer = true
            const canPlay = this.checkAndPlayAutoplayGrid()
            if (canPlay) {
                this.ftStreamingPlayer.play()
                this.fadeInVolume(1500); // Trigger fade-in over 1.5 seconds
                if (playState.playTrailer) {
                    const { appState } = AppData as AppSetup;
                    if (appState && appState.player) {
                        if (!appState.player.trailersPlayedInSession) appState.player.trailersPlayedInSession = [];
                        const nodeId = videoToPlay?.node_id;
                        if (nodeId && !appState.player.trailersPlayedInSession.includes(nodeId)) {
                            appState.player.trailersPlayedInSession.push(nodeId);
                        }
                    }
                }
            }

        }, isBlocked ? this.blockedItemDelay : this.config.gridDelay)
    }

    /**
     * @method startLiveAutoplay
     * @description Autoplay flow for a focused live item.
     * Live content is never previewed inside the hero image section - instead, once `gridDelay` elapses
     * the user is taken straight to the live video screen via the `onVideoAutoplay` event.
     * - Honours the same guards as regular autoplay (grid autoplay switch, session ignore list, blocked items).
     * - Re-validates focus and widget state when the timer fires, so a focus change or an opened widget
     *   during the countdown cancels the jump.
     * - Marks the channel as consumed for the session so backing out of the player and landing on the
     *   same item does not autoplay it again.
     */
    private startLiveAutoplay(vodInfo: ItemNode) {
        const { appConfig, appState } = AppData as AppSetup;
        const app = appState?.getApp()

        if (appConfig?.enableLiveAutoplay === false) {
            Log.info('Autoplay: live autoplay disabled by app config');
            return
        }

        if (!this.isGridAutoplayEnabled()) {
            Log.info('Autoplay: live autoplay skipped because grid autoplay is disabled');
            return
        }

        if (this.isAutoplayIgnored(vodInfo)) {
            Log.info('Autoplay: live item already autoplayed in this session', vodInfo.node_id);
            return
        }

        if (this.isItemBlockedForAutoplay(app, appConfig, vodInfo)) {
            Log.info(`Autoplay: Blocked live item focused. Waiting ${this.blockedItemDelay / 1000}s to auto-shift.`);
            const currentGrid = app?.navigation.getCurrentGrid()
            this.gridTimeout = window.setTimeout(() => {
                if (currentGrid) {
                    const focusInfo = currentGrid.getFocusInfo();
                    const rowLength = currentGrid._getRowItemLength(focusInfo.focusRow);
                    this.scanAndSkipBlockedRowItems(currentGrid, app, appConfig, focusInfo, rowLength);
                }
            }, this.blockedItemDelay)
            return
        }

        this.isLiveAutoplayPending = true
        this.emit("onStart")
        Log.info('Autoplay: , LiveGridTimerStart', performance.now());

        this.gridTimeout = window.setTimeout(() => {
            this.isLiveAutoplayPending = false

            const activeWidget = Router.getActiveWidget()
            if (activeWidget) {
                Log.info('Autoplay: live autoplay aborted, a widget became active', activeWidget);
                return
            }

            const currentVodInfo = app?.navigation.getCurrentVodInfo() as ItemNode
            if (!currentVodInfo || currentVodInfo.node_id !== vodInfo.node_id) {
                Log.info('Autoplay: live autoplay aborted, focus changed during the countdown');
                return
            }

            this.markAutoplayIgnored(vodInfo)
            this.isAutoplayStart = true
            Log.info('Autoplay: , LiveGridTimerExecuted', performance.now());
            this.emit("onVideoAutoplay", vodInfo)
        }, this.config.gridDelay)
    }

    /**
     * @method transitionToMainPlayer
     * @description Transition the playback experience from the grid preview to the fullscreen main player.
     * Called automatically either when standard autoplay preview time elapses or a trailer finishes.
     */
    transitionToMainPlayer() {
        Log.info('Autoplay: , Transitioning to Main Player');
        const { appConfig, appState } = AppData as AppSetup;
        const enableTrailer = appState?.getAppApiConfig()?.appConfig?.enableTrailer;
        const isTrailer = (appConfig?.enableTrailer && this.playingVodInfo?.trailer_url && enableTrailer);
        
        const curTime = Math.round(this.getCurrentTime());
        this.isAutoplayStart = true;
        this.updateMlpStack();
       
        // Store the current time if it's not a trailer
        if (!isTrailer) {
            if (appConfig?.useLocalStorage == true) {
                localStorage.setItem(this.playingVodInfo.node_id, curTime.toString());
            } else {
                Helper.setGeneralCookie(this.playingVodInfo.node_id, curTime.toString(), 365);
            }
        }
        
        this.markAutoplayIgnored(this.playingVodInfo);

        this.emit("onAutoplayStopped", this.playingVodInfo, isTrailer);
        this.clearVideoData();
        this.emit("onVideoAutoplay", this.playingVodInfo);
    }
    /** 
    * @method userInteracted
    * @description Handle user interaction with the autoplay functionality.
    * It stops the autoplay and clears the video data if required.
    * @param {boolean} stopOnInteraction - Flag to stop autoplay on user interaction.
    */
    userInteracted(stopOnInteraction: boolean = false) {
        Log.info('User interacted with autoplay', stopOnInteraction)
        if (this.isAutoplayStart || this.isAutoPlayGridTimer) {
            const videoToPlay = this.playingVodInfo || this.focusVodInfo;
            if (videoToPlay && this.playState) {
                this.emit("onAutoplayStopped", videoToPlay, this.playState.playTrailer);
            }
        }
        this.stop()
        this.clearVideoData()
        this.isAutoplayStart = false
        if (stopOnInteraction) {
            this.handleStartDebounced()
        } else {
            this.stop()
        }
    }

    /**
     * @method registerBackPress
     * @description Tracks back presses during autoplay. Disables autoplay for 10 minutes if 3 presses occur within 120 seconds.
     */
    registerBackPress() {
        if (!this.isAutoplayStart && !this.isAutoPlayGridTimer && !this.isLiveAutoplayPending) return;

        const now = Date.now();
        this.autoplayBackPresses.push(now);
        
        // Keep only presses within the last 120 seconds
        this.autoplayBackPresses = this.autoplayBackPresses.filter(time => now - time <= 120000);
        
        if (this.autoplayBackPresses.length >= 3) {
            Log.info('Autoplay disabled for 10 minutes due to 3 consecutive back presses');
            this.autoplayDisabledUntil = now + 10 * 60 * 1000;
            this.autoplayBackPresses = [];
            this.stop();
            this.clearVideoData();
        }
    }

    /**
     * @method isAutoplayTemporarilyDisabled
     * @description Checks if autoplay is currently under a 10 minute penalty.
     */
    isAutoplayTemporarilyDisabled(): boolean {
        if (this.autoplayDisabledUntil) {
            if (Date.now() < this.autoplayDisabledUntil) {
                Log.info('Autoplay is temporarily disabled for 10 minutes.');
                return true;
            } else {
                this.autoplayDisabledUntil = null;
            }
        }
        return false;
    }

    /**
     * @method scanAndSkipBlockedRowItemsInMlp
     * @description Asynchronously scans the current row inside an MLP grid to find an unblocked episode.
     */
    private async scanAndSkipBlockedRowItemsInMlp(mlpGrid: any, app: any, appConfig: any) {
        if (!mlpGrid) return null;
        const focusInfo = mlpGrid.getFocusInfo();
        const rowLength = mlpGrid._getRowItemLength(focusInfo.focusRow);
        
        for (let i = focusInfo.foucsItem + 1; i < rowLength; i++) {
            const nextItem = await mlpGrid.getItemAt(focusInfo.focusRow, i);
            if (!nextItem) continue;

            if (nextItem.feed_type == 'live') {
                Log.info('Autoplay (MLP): Encountered live content while scanning. Halting scan.');
                return null;
            }

            if (!this.isItemBlockedForAutoplay(app, appConfig, nextItem)) {
                Log.info(`Autoplay (MLP): Found unblocked episode at index ${i}. Changing focus.`);
                mlpGrid.changeFocus({ focusRow: focusInfo.focusRow, foucsItem: i });
                return nextItem;
            }
        }
        return null;
    }

    /**
     * @method scanAndSkipBlockedRowItems
     * @description Asynchronously scans the current row starting from the next item to find an unblocked item, and changes UI focus to it.
     */
    private async scanAndSkipBlockedRowItems(currentGrid: any, app: any, appConfig: any, focusInfo: any, rowLength: number) {
        let foundUnblockedIndex = -1;
        for (let i = focusInfo.foucsItem + 1; i < rowLength; i++) {
            const nextItem = await currentGrid.getItemAt(focusInfo.focusRow, i);
            if (nextItem && !this.isItemBlockedForAutoplay(app, appConfig, nextItem)) {
                foundUnblockedIndex = i;
                break;
            }
        }

        if (foundUnblockedIndex !== -1) {
            Log.info(`Autoplay: Found unblocked item at index ${foundUnblockedIndex}. Changing focus.`);
            currentGrid.changeFocus({ focusRow: focusInfo.focusRow, foucsItem: foundUnblockedIndex });
            
            // Sync UI visually
            const activePage: any = typeof Router.getActivePage === 'function' ? Router.getActivePage() : Router.getActiveWidget();
            if (activePage && typeof activePage.resetFocus === 'function') {
                activePage.resetFocus({ focusRow: focusInfo.focusRow, foucsItem: foundUnblockedIndex });
            }
        } else {
            Log.info('Autoplay: All remaining items in the row are blocked. Staying put.');
        }
    }

    /** 
    * @method checkAndPlayAutoplayGrid
    * @description Check if the autoplay grid video is ready and play it if so.
    */
    checkAndPlayAutoplayGrid() {
        if (this.isAutoPlayGridVideoReady == true && this.isAutoPlayGridTimer == true) {
            Log.info('Autoplay: , checkAndPlayAutoplayGrid', performance.now());

            return true
        }
        return false
    }

    /**
     * @method fadeInVolume
     * @description Fades the volume of the video player from 0 to 1 over the specified duration.
     * @param {number} duration - The duration of the fade-in effect in milliseconds (default 1500).
     */
    fadeInVolume(duration: number = 1500) {
        if (!this.videoEl) return;
        
        this.videoEl.volume = 0;
        const steps = 20;
        const stepTime = duration / steps;
        let currentStep = 0;

        const fadeInterval = setInterval(() => {
            if (!this.videoEl) {
                clearInterval(fadeInterval);
                return;
            }
            
            currentStep++;
            let newVolume = currentStep / steps;
            if (newVolume >= 1) {
                newVolume = 1;
                clearInterval(fadeInterval);
            }
            
            // Catching any potential errors if the video element state changes
            try {
                this.videoEl.volume = newVolume;
            } catch (e) {
                clearInterval(fadeInterval);
            }
        }, stepTime);
    } 
 
    /** 
    * @method stop
    * @description Stop the autoplay functionality.
    * It clears the timeouts for grid and video autoplay.
    * 
    */
    stop() {
        this.isAutoPlayGridTimer = false
        this.isAutoPlayGridVideoReady = false
        this.isLiveAutoplayPending = false
        if (this.gridTimeout) {
            clearTimeout(this.gridTimeout)
            this.gridTimeout = null
            if (this.isAutoplayStart) {
                this.clearVideoData()
            }
        }
        if (this.videoTimeout) {
            clearTimeout(this.videoTimeout)
            this.videoTimeout = null
            if (this.isAutoplayStart) {
                this.clearVideoData()
            }
        }
    }
    
    /** 
     * @method handleStartDebounced
    * @description Handle the start of the autoplay with a debounce effect.
    * It ensures that the function is not called too frequently.
    * It sets up the timeouts for grid and video autoplay.
    * 
    */
   handleStartDebounced() {
        if (this.handleStartDebounceTimer) {
            clearTimeout(this.handleStartDebounceTimer);
        }
        this.handleStartDebounceTimer = window.setTimeout(() => {
            this.start()
            this.handleStartDebounceTimer = null;
        }, 1000);
    }
    /** 
     * @method preloadVideo
    * @description Preload the video with the provided video data.
    * It sets the video information and handles the resume time for playback.
    * @param {ItemNode} videoData - The video information object.
    * 
    */
    preloadVideo(videoData: ItemNode) {
        if (!this.contentPlayer) {
            return
        }
        // Live content must never be previewed inside the hero image section.
        if (this.isLiveItem(videoData)) {
            Log.info('Autoplay: preview skipped for live content.');
            return
        }
        if (videoData.video_format === 'mp4' || videoData.trailer_format === 'mp4') {
            setTimeout(() => {
                this.stop()
            }, 100);
            Log.info('Autoplay disabled for mp4 content.');
            return
        }
        this.clearVideoData()
        this.playingVodInfo = videoData
        
        this.playState = this.getPlayState(videoData);
        if (!this.playState.shouldPlay) {
            Log.info('Autoplay disabled: constraints not met for autoplay.');
            return;
        }

        let videoDataToPlay = { ...videoData };
        if (!this.playState.playTrailer) {
            videoDataToPlay.trailer_url = undefined;
        }

        const { appConfig } = AppData as AppSetup;
        let resumeTime: any = 0
        if (appConfig?.useLocalStorage == true) {
            resumeTime = localStorage.getItem(videoData.node_id);
        } else {
            resumeTime = Helper.getCookie(videoData.node_id);
        }
        resumeTime = parseInt(resumeTime);
        if (resumeTime == 0 || Number.isNaN(resumeTime)) {
            resumeTime = videoData.intro_et ? (videoData.intro_et + 3) : resumeTime
        }
        this.contentPlayer.setVideoInfo(videoDataToPlay)
        this.contentPlayer?._resumePlayer(resumeTime, true)
        this.contentPlayer._setEventListeners(this.handleVideoEvent)
        this.setGridPlayerSize()
    }
    
    /** 
     * @method handleVideoEvent
     * @description Handle the video events such as canplay and ended.
     * It checks if the video is ready for immediate playback and handles the end of the video.
     * @param {Event} event - The video event object.
     * 
    */
    handleVideoEvent = (event) => {
        if (event.type !== 'progress' && event.type !== 'timeupdate') { Log.info('PlayerEvent:', event.type) }

        // if (event.type === 'playererror' || event.type === 'error') {
        //     Log.info('Autoplay: player error encountered. Stopping autoplay.');
        //     this.stop();
        //     this.emit("onAutoplayStopped");
        //     this.clearVideoData();
        //     return;
        // }

        if (event.type === 'canplay' && !this.isAutoPlayGridVideoReady) {
            Log.info('Autoplay: , GridVideoReady', performance.now());
            this.isAutoPlayGridVideoReady = true
            const canPlay = this.checkAndPlayAutoplayGrid()
            Log.info('Autoplay: , GridVideoReady - canPlay', canPlay);

            setTimeout(() => {
                if (!canPlay) {
                    this.ftStreamingPlayer.pause()
                    return
                }
                this.ftStreamingPlayer.play()
                this.fadeInVolume(1500);
                if (this.playState.playTrailer) {
                    const { appState } = AppData as AppSetup;
                    if (appState && appState.player) {
                        if (!appState.player.trailersPlayedInSession) appState.player.trailersPlayedInSession = [];
                        const nodeId = this.playingVodInfo?.node_id;
                        if (nodeId && !appState.player.trailersPlayedInSession.includes(nodeId)) {
                            appState.player.trailersPlayedInSession.push(nodeId);
                        }
                    }
                }
            }, 100)
        }

        const isTrailer = this.playState.playTrailer;

        if (isTrailer && event.type === 'ended') {
            if (this.playState.transition) {
                Log.info('Autoplay: Trailer finished naturally. Transitioning to Main Player.');
                this.transitionToMainPlayer();
            } else {
                Log.info('Autoplay: Trailer finished naturally. Staying on grid.');
                this.emit("onAutoplayStopped", this.playingVodInfo, isTrailer);
                this.clearVideoData();
            }
        }

        if (!isTrailer && this.isAutoPlayGridTimer) {
            if (event.type === 'timeupdate' && this.videoEl) {
                if (this.previewStartTime === null) {
                    this.previewStartTime = this.videoEl.currentTime;
                }
                const accruedTime = this.videoEl.currentTime - this.previewStartTime;
                if (accruedTime >= 10) {
                    if (this.playState.transition) {
                        this.transitionToMainPlayer();
                    } else {
                        Log.info('Autoplay: transition is false. Staying on grid after preview.');
                        this.emit("onAutoplayStopped", this.playingVodInfo, isTrailer);
                        this.clearVideoData();
                    }
                }
            }
        }
    }
 

    /** 
    * @method clearVideoData
    * @description Clear the video data by resetting the video player.
    * 
    */
    clearVideoData() {
        this.previewStartTime = null;
        if (this.contentPlayer) {
            this.contentPlayer._removeEventListener(this.handleVideoEvent)
            this.contentPlayer.resetPlayer()
        }
    }

    /** 
    * @method getCurrentTime
    * @description Get the current time of the video player.
    * @returns {number} - The current time of the video player.
    * 
    */
    getCurrentTime = (): number => {
        if (this.videoEl) {
            return this.videoEl.currentTime;
        }
        return 0;
    }

    /** 
    * @method getAutoplayItem
    * @description Get the autoplay item based on the current grid or video.
    * It checks if the item is a list of lists or a video and sets up the content player accordingly.
    * 
    */
    async getAutoplayItem() {
        const { appState, appConfig } = AppData as AppSetup
        const app = appState?.getApp()
        const vodInfo = app?.navigation.getCurrentVodInfo() as ItemNode
        let mlpInfo = null
        this.resetMlpStack()
        if (vodInfo) {
            if (vodInfo.type == 'listoflist' || vodInfo.type == 'new-qid' || vodInfo.feed_type == 'banner') {
                // const mlpGrid = app?.loadShow(vodInfo, vodInfo.id, false)
                // await mlpGrid?.gridModel?.fetchInitialRows()
                // mlpGrid?.createGridListUI()
                try {
                    mlpInfo = await this.loadMlp(vodInfo) as ItemNode
                    
                    // Verify the user hasn't changed focus while we were loading MLP
                    const currentVodInfo = app?.navigation.getCurrentVodInfo() as ItemNode;
                    const isSameItem = (currentVodInfo?.node_id && currentVodInfo.node_id === vodInfo.node_id) || 
                                       (currentVodInfo?.id && currentVodInfo.id === vodInfo.id);

                    if (currentVodInfo && !isSameItem) {
                        Log.info('Autoplay: Focus changed during loadMlp. Aborting resolution for old item.');
                        this.resetMlpStack();
                        return;
                    }

                    if (!mlpInfo) {
                        Log.info('Autoplay (MLP): Required episode was blocked or missing. Navigating to nested grid.');
                        this.stop();
                        this.clearVideoData();
                        this.resetMlpStack();
                        Router.navigate(`mlp-grid-screen/${vodInfo.id}`, false);
                        return;
                    }
                    if (app?.blockingManager.isItemBlocked(mlpInfo)) {
                        this.stop()
                        this.clearVideoData()
                        this.resetMlpStack()
                        Log.info('Autoplay paused for blocked item');
                    } else {
                        this.preloadVideo(mlpInfo)
                    }
                    
                } catch (el) {
                    Log.info('getAutoplayItem error', el);
                }
                          
            } else if (vodInfo.feed_type == 'video') {
                if (this.focusVodInfo === vodInfo) {
                    this.preloadVideo(vodInfo)
                }  
            }
        }
    }

    /** 
    * @method setGridPlayerSize
    * @description Set the size of the grid player based on the screen size.
    * It resizes the player to fit the screen and sets the autoplay start flag.
    * 
    */
    setGridPlayerSize() {
        const { appConfig } = AppData as AppSetup;
        const rowType = this._getCurrentRowType()
        const isCarouselOrNonClick = appConfig?.enableMidCarousel && (rowType === 'carousel' || rowType === 'non-click')

        if (isCarouselOrNonClick) {
            const midCarouselWidth = Math.round((800 / 1920) * 100);
            const midCarouselHeight = Math.round((450 / 1080) * 100);
            Helper.resizePlayer(this.videoEl, 9.5, 14.4, midCarouselWidth, midCarouselHeight);
            this.contentPlayer.showCarouselPreview();
        } else {
            const resizePercent = Math.round((1244 / 1920) * 100);
            const xPos = Math.round(((1920 - 1244) / 1920) * 100);
            Helper.resizePlayer(this.videoEl, xPos, 0, resizePercent, resizePercent);
            this.contentPlayer.resetCarouselPreview();
            if (this.videoEl) {
                this.videoEl.style.opacity = '1';
            }
        }
        
        this.isAutoplayStart = true;
    }

    /** 
    * @method loadMlp
    * @description Load the mlp (multi-level player) with the provided video information.
    * It initializes the mlp grid and checks if the mlp info is a list of lists or a new qid.
    * It recursively calls loadMlp if required.
    * @param {ItemNode} _vodInfo - The video information object.
    * @return {Promise<ItemNode | null>} - The mlp information object or null.
    * 
    */
    async loadMlp(_vodInfo: ItemNode): Promise<ItemNode | null> {

        const { appState } = AppData as AppSetup;
        const app = appState?.getApp()
        const mlpGrid: GridViewModel = (app?.loadShowPrefetch(_vodInfo, _vodInfo.id))!
        await mlpGrid._init()
        mlpGrid.createGridListUI()
        this.mlpStack.push(mlpGrid)
        let mlpInfo = mlpGrid.getFocusedItem()

        try {
            const bdata = await app?.bookmarkingManager.checkBookmarkAndLoadShows(_vodInfo)
            if (bdata && bdata.length > 1 && bdata[1]) {
                const epInfo = bdata[1];
                if (epInfo.season !== null && epInfo.episode !== null && epInfo.season !== '' && epInfo.episode !== '') {
                    Log.info('Autoplay: Resume episode found: ', _vodInfo.title, epInfo.season, epInfo.episode);
                    await mlpGrid.findAndLoadSeasonEpisode(epInfo.season, epInfo.episode);
                    mlpInfo = mlpGrid.getFocusedItem() || mlpInfo;
                }
            }
        } catch (e) {
            Log.error('Autoplay: error in checkBookmarkAndLoadShows', e);
        }

        const { appConfig } = AppData as AppSetup;
        // A live episode can never be previewed in the hero, so treat it the same as a blocked one here
        // and let the MLP scan look for a playable sibling instead.
        if (mlpInfo && (this.isItemBlockedForAutoplay(app, appConfig, mlpInfo) || this.isLiveItem(mlpInfo))) {
            mlpInfo = await this.scanAndSkipBlockedRowItemsInMlp(mlpGrid, app, appConfig);
        }

        //check if mlpInfo is again listoflist or new-qid or banner then recursively call loadMlp
        if (mlpInfo && (mlpInfo.type == 'listoflist' || mlpInfo.type == 'new-qid' || mlpInfo.feed_type == 'banner')) {
            const parentGrid = this.mlpStack[this.mlpStack.length - 1]
            mlpInfo = await this.loadMlp(mlpInfo, parentGrid) as ItemNode
        }

        // console.log('loadMlp mlpInfo', this.mlpStack, mlpInfo);
        return mlpInfo
    }

    /** 
    * @method updateMlpStack
    * @description Update the mlp stack by adding the current mlp grid to the continues stack.
    * It ensures that the mlp stack is not empty and logs the before and after state of the mlp stack.
    * 
    */
    updateMlpStack() {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp()
        if(this.mlpStack.length == 0) return

        app.navigation.continuesStack = [...app.navigation.continuesStack, ...this.mlpStack]
    }

    /** 
    * @method resetMlpStack
    * @description Reset the mlp stack by destroying all mlp instances in the stack.
    * It clears the mlp stack and destroys the mlp instances to free up resources.
    * 
    */
    resetMlpStack() {
        if(this.mlpStack.length > 0) {
            for(let i = this.mlpStack.length-1; i >= 0; i--) {
                const mlp = this.mlpStack.pop()
                if(mlp) {
                    mlp.destroy()
                }
            }
        }
    }

    /** 
    * @method destroy
    * @description Destroy the autoplay instance and clear the video data.
    * It removes all listeners and resets the video player.
    */
    destroy() {
        this.stop()
        if (this.contentPlayer && this.ftStreamingPlayer) {
            Helper.resizePlayer(this.videoEl, 0, 0, '100', '100')
            this.contentPlayer._removeEventListener(this.handleVideoEvent)
            this.clearVideoData()
            this.videoEl = null
            this.videoElWrapper = null
            this.contentPlayer = null
        }

    }
}